import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { usePopupProfile } from 'ogp/components/views/planning/hooks';
import { RelationType } from 'ogp/hooks/queryHooks/flexWorkers/useGetFlexWorkerProfile';
import { useDebounce } from 'shared/hooks/use-debounce';
import { useRemoveWorkerFromWorkersPool } from 'ogp/hooks/queryHooks/workersPools/use-remove-worker-from-workers-pool';
import { useAddWorkerToWorkersPool } from 'ogp/hooks/queryHooks/workersPools/use-add-worker-to-workers-pool';
import { useAlert } from 'shared/components/alerts';
import { DeleteButton, PrimaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { IconCancel } from 'assets/img';
import { invariant } from 'shared/utils/utils';
import { useQueryClient } from 'react-query';
import { QUERY_KEYS } from 'api/query-keys';
import { useGetWorkersPoolFlexWorkers } from './use-get-workers-pool-flex-workers';
import { FlexPoolAddFlexWorker } from '../../../../flex-pool-detail/components/flex-workers/components/flex-pool-add-flex-worker';
import { FlexpoolType } from '../../../../flex-pool-detail/components/flex-workers/hooks/use-get-available-flex-workers-for-pool';

type AddWorkerFormValues = { flexWorker: { id: string; email: string } };

export const useWorkersPoolFlexWorkersHandlers = (workersPoolId: string) => {
  const { t } = useTranslation('ogp-screen-specific');
  const { alertSuccess, alertError } = useAlert();
  const queryClient = useQueryClient();
  const [nameInputValue, setNameInputValue] = useState('');
  const { open: openPopupProfile, Element: PopupProfileElement } = usePopupProfile();
  const debouncedNameInput = useDebounce(nameInputValue, 300);
  const { data, error, refetch, status } = useGetWorkersPoolFlexWorkers(
    workersPoolId,
    debouncedNameInput.length > 0 ? debouncedNameInput : undefined
  );
  const { mutate: removeWorkerMutate } = useRemoveWorkerFromWorkersPool();
  const addWorkerToWorkersPool = useAddWorkerToWorkersPool();
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const addWorkerFormMethods = useForm<AddWorkerFormValues>({
    defaultValues: {
      flexWorker: { id: '', email: '' },
    },
  });

  const handleShowFlexWorker = (id: string) => {
    openPopupProfile(id, {
      relation: RelationType.WORKERS_POOL,
      id: workersPoolId,
    });
  };

  const removeWorker = ({ poolId, flexWorkerId }: { poolId: string; flexWorkerId: string }) => {
    removeWorkerMutate(
      { workersPoolId: poolId, flexWorkerId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(QUERY_KEYS.flexPools());
          alertSuccess(t('flexpools.external.alerts.removed_worker'));
          closeDialog();
        },
        onError: (e) => alertError(e),
        onSettled: () => {
          enableDialog();
        },
      }
    );
  };

  const handleRemoveFlexWorker = (id: string) => {
    invariant(data);

    const flexWorker = data.find((item) => item.id === id);

    invariant(flexWorker);

    openDialog(
      {
        title: t('flexpools.external.workers.remove_dialog.title'),
        content: t('flexpools.external.workers.remove_dialog.confirm', { name: flexWorker.name }),
        secondaryButton: (props) => (
          <PrimaryButton iconColorMode={'fill'} startIcon={<IconCancel />} {...props}>
            Annuleer
          </PrimaryButton>
        ),
        primaryButton: (props) => <DeleteButton {...props} />,
      },
      () => {
        disableDialog();
        removeWorker({ poolId: workersPoolId, flexWorkerId: id });
      }
    );
  };

  const submitAddWorkerData = (formData: AddWorkerFormValues) => {
    const { flexWorker } = formData;
    addWorkerToWorkersPool.mutate(
      {
        workersPoolId,
        flexWorkerId: flexWorker.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(QUERY_KEYS.flexPools());
          alertSuccess(t('flexpools.external.alerts.added_worker'));
          closeDialog();
          addWorkerFormMethods.reset();
        },
        onError: (e) => alertError(e),
      }
    );
  };

  const handleAddFlexWorker = () => {
    addWorkerFormMethods.reset({
      flexWorker: { id: '', email: '' },
    });
    openDialog(
      {
        title: t('flexpools.external.workers.add_dialog.title'),
        content: (
          <FormProvider {...addWorkerFormMethods}>
            <FlexPoolAddFlexWorker flexpoolType={FlexpoolType.External} poolId={workersPoolId} />
          </FormProvider>
        ),
      },
      () => addWorkerFormMethods.handleSubmit(submitAddWorkerData)()
    );
  };

  return {
    nameInputValue,
    setNameInputValue,
    data,
    error,
    refetch,
    status,
    PopupProfileElement,
    handleShowFlexWorker,
    handleRemoveFlexWorker,
    handleAddFlexWorker,
  };
};
